import { useState, useEffect } from 'react';
import { Activity as ActivityIcon, User, Folder, FileText, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const ActivityFeed = ({ user }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/activities?limit=50`, {
      });

      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities);
      }
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (action) => {
    if (action.includes('user')) return User;
    if (action.includes('project')) return Folder;
    if (action.includes('asset')) return FileText;
    if (action.includes('approve')) return Check;
    return ActivityIcon;
  };

  const getActivityColor = (action) => {
    if (action.includes('create')) return 'text-positive';
    if (action.includes('approve')) return 'text-brand';
    if (action.includes('login')) return 'text-brand';
    return 'text-brand';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activity-feed">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-semibold tracking-tight text-text-primary mb-2">The Hub: Activity Feed</h2>
        <p className="text-text-secondary">Real-time updates from across the platform</p>
      </div>

      {/* Activity Timeline */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-brand">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4" data-testid="activity-list">
            {activities.map((activity) => {
              const Icon = getActivityIcon(activity.action);
              const colorClass = getActivityColor(activity.action);

              return (
                <div
                  key={activity.id}
                  className="flex items-start gap-4 p-4 rounded-xl bg-white/50 hover:bg-white/70 transition-colors"
                  data-testid={`activity-${activity.id}`}
                >
                  <div className={`w-10 h-10 rounded-full bg-white flex items-center justify-center ${colorClass}`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="text-text-primary font-medium">{activity.description}</p>
                    <p className="text-sm text-text-secondary mt-1">
                      {new Date(activity.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {activities.length === 0 && (
            <div className="text-center py-12">
              <ActivityIcon className="mx-auto text-text-tertiary mb-4" size={48} />
              <h3 className="text-xl font-semibold text-text-primary mb-2">No activity yet</h3>
              <p className="text-text-secondary">Activity will appear here as users interact with the system</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityFeed;
